<script setup lang="ts">
import { computed } from 'vue';

// One tile in a top bar dropdown grid. Renders as a link when given an href, otherwise as a
// button — shared so the platform switcher and the mobile apps menu stay visually identical.
const props = defineProps({
  label: {
    type: String,
    required: true
  },
  /** Bundled logo. Falls back to an initials tile, then to an icon, when absent. */
  logo: {
    type: String,
    default: ''
  },
  /** FontAwesome icon used instead of a logo, as `[style, name]`. */
  icon: {
    type: Array as unknown as () => [string, string] | null,
    default: null
  },
  href: {
    type: String,
    default: ''
  },
  /** Small line under the label — "Current", the store name, and so on. */
  caption: {
    type: String,
    default: ''
  },
  highlighted: {
    type: Boolean,
    default: false
  },
  openInNewTab: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

const tag = computed(() => (props.href ? 'a' : 'button'));

// Matches how the study selector renders a study with no artwork.
const initials = computed(() =>
  props.label
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase()
);
</script>

<template>
  <component :is="tag"
    class="app-tile"
    :class="{ 'app-tile--highlighted': highlighted }"
    :href="href || undefined"
    :type="tag === 'button' ? 'button' : undefined"
    :target="openInNewTab ? '_blank' : undefined"
    :rel="openInNewTab ? 'noopener noreferrer' : undefined"
    @click="emit('click', $event)">
    <img v-if="logo"
      :src="logo"
      alt=""
      class="app-tile__logo" />
    <span v-else-if="icon" class="app-tile__logo app-tile__logo--icon">
      <FontAwesomeIcon :icon="icon" />
    </span>
    <span v-else class="app-tile__logo app-tile__logo--initials">{{ initials }}</span>

    <span class="app-tile__label">{{ label }}</span>
    <span v-if="caption" class="app-tile__caption">{{ caption }}</span>
  </component>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.app-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 104px;
  padding: 12px 8px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: hsl(210, 11%, 15%);
  font: inherit;
  text-align: center;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: hsl(210, 20%, 96%);
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: -2px;
  }
}
.app-tile--highlighted {
  background-color: hsl(248.5, 100%, 97%);
}
.app-tile__logo {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: contain;
}
.app-tile__logo--icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(210, 11%, 25%);
  font-size: 26px;
}
.app-tile__logo--initials {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $primary-color;
  color: white;
  font-size: 13px;
  font-weight: 600;
}
.app-tile__label {
  font-size: 13px;
  line-height: 16px;
}
.app-tile__caption {
  font-size: 11px;
  line-height: 14px;
  color: hsl(248.5, 100%, 45%);
}
</style>
